/**
 * Parkify Patrol — two screens: pick a lot, then work it.
 *
 * Read-only. The app cannot issue anything; it answers "is this car allowed to
 * be here" and hands the decision back to the officer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGoogleLogin } from '@react-oauth/google'
import {
  claimCredential, pendingSetupLink, enrollFromSetupLink, fetchZones, fetchRoster, checkPlate, checkBay, type BayResult,
  readStaffSession, hasLapsedStaffSession, forgetStaffSession, signInWithGoogle, callerKindOf, pickerEntries,
  type FacilityZoneAnswer, type FacilityZoneState,
  PatrolAuthError, PatrolNotGrantedError, PatrolRequestError, type CallerKind, type Snapshot, type Vehicle, type ZoneRef, type Candidate,
  type ZoneClosure, type Verdict,
} from './api'
import {
  toneFor, resultToneFor, labelFor, subLabelFor, endingSoon, withLiveMinutes, headlineFor, guidanceFor, guidanceParts,
  plateStateMarkFor, plateStateMarkLabel, plateStateMarkTitle, plateDiffMask, rosterMatch, glossaryEntries, verdictMeaning,
  rowAccessibleName, candidateAccessibleName, keepTimesTogether, capitalizeReason, splitClosedGuidance, lapseLine,
  facilityZoneLine,
} from './verdict'

type Filter = 'all' | 'live' | 'attention'

/**
 * Where the answer on the result card came from. `checked` is a lookup the
 * server actually answered; `roster` is the snapshot's own answer, dated by the
 * snapshot rather than by the tap.
 */
type Provenance = { kind: 'checked' | 'roster'; at: Date }

/**
 * Lot policy carried on a plate response (#3339): the operator's arrival grace
 * and the lot timezone. Each field is optional on the wire, so Pages can deploy
 * ahead of the Worker; `undefined` means "not sent, use the roster's copy".
 */
/**
 * What a plate response reported beside the verdict. `zones` is a facility
 * lookup's per-zone answers; it lives here so it is cleared everywhere the rest
 * of the lookup is, and can never sit under a different car's card.
 */
type LookupPolicy = { arrivalGraceMinutes?: number | null; timezone?: string | null; zones?: FacilityZoneAnswer[] }
function policyOf(r: { arrivalGraceMinutes?: number | null; timezone?: string | null; zones?: FacilityZoneAnswer[] }): LookupPolicy {
  return { arrivalGraceMinutes: r.arrivalGraceMinutes, timezone: r.timezone, ...(r.zones ? { zones: r.zones } : {}) }
}

/** Remember the lot between app opens — an officer works the same lot all shift. */
const LAST_LOT_KEY = 'parkify.patrol.lastLot'

/**
 * When the roster stops being safe to act on without refreshing, in three
 * stages (Austin's demo, owner decision 2026-10-01): live (green), then from
 * 60 seconds "refresh before enforcing" in amber, then from 180 seconds red.
 *
 * A minute is roughly the window in which a driver can pay and still be booted
 * on the strength of a stale screen, the one mistake here that costs a real
 * customer. It deliberately does NOT block the officer or auto-refresh under
 * them: it says the age out loud and offers the refresh.
 */
const STALE_AFTER_SEC = 60
const HARD_STALE_AFTER_SEC = 180

/**
 * A failed read, as the screens keep it. An OBJECT on purpose: this used to be the
 * error's message string, and screens gated their note on that string being
 * truthy, so a failure whose message came back '' (an HTML 502 over HTTP/2) drew
 * nothing and left the screen on "Loading…" forever. An object is truthy however
 * little it says. The message itself is never shown: it can be a browser's
 * "Failed to fetch" or server prose, so screens write their own plain copy and
 * only branch on whether the phone had a connection at all.
 */
interface LoadFailure { offline: boolean }
function failureOf(e: unknown): LoadFailure {
  return { offline: e instanceof PatrolRequestError && e.offline }
}

/**
 * The roster's age in the demo's words: "just now", "21s ago", "1m 6s ago",
 * "1h 20m ago" (Austin's demo, owner decision 2026-10-01: seconds, not rounded
 * minutes, so an officer can see how old the screen really is).
 */
function ageLabel(sec: number): string {
  if (sec < 5) return 'just now'
  if (sec < 60) return `${sec}s ago`
  const mins = Math.floor(sec / 60)
  const s = sec % 60
  if (mins < 60) return s ? `${mins}m ${s}s ago` : `${mins}m ago`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m ? `${h}h ${m}m ago` : `${h}h ago`
}

function readLastLot(): string | null {
  try { return localStorage.getItem(LAST_LOT_KEY) } catch { return null }
}
function writeLastLot(id: string | null): void {
  try {
    if (id) localStorage.setItem(LAST_LOT_KEY, id)
    else localStorage.removeItem(LAST_LOT_KEY)
  } catch { /* private mode — the picker just shows every time */ }
}

/**
 * The wordmark. The header carries the brand-colour one (purple stacked-layers mark
 * beside the dark wordmark, the same asset the checkout and the tenant portal ship,
 * and the one the demo's patrol header shows); the sign-in card keeps the single
 * dark one on its white card.
 */
function Logo({ className, color = false }: { className: string; color?: boolean }) {
  return <img src={color ? '/parkify-logo-color.svg' : '/parkify-logo.svg'} alt="Parkify" className={className} />
}

/**
 * No usable credential. For an enforcement partner this is terminal — the fix is
 * a new link, never a retry loop. Parkify staff get a way IN from here: they have
 * no link to open, they sign in.
 *
 * The Google button only renders when the OAuth client is configured, so an
 * unconfigured build shows exactly the screen a partner used to see rather than a
 * button that can't work.
 */
function Gate({ title, body, children }: { title: string; body: string; children?: React.ReactNode }) {
  return (
    <div className="gate">
      <div className="gate__card">
        <Logo className="gate__logo" />
        <div className="gate__product">PATROL</div>
        <h1 className="gate__title">{title}</h1>
        <p className="gate__body">{body}</p>
        {children}
      </div>
    </div>
  )
}

const GOOGLE_CONFIGURED = !!(import.meta.env.VITE_AUTH_GOOGLE_CLIENT_ID as string | undefined)

/**
 * Google sign-in for a signed-in person: Parkify staff, or an operator's own
 * team member who has been given plate checks. Same implicit flow, same
 * `/api/auth/google` exchange and same button wording as the console: one auth
 * path to reason about, not two. Which lots they get is the server's answer.
 */
function StaffSignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const login = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (t) => {
      setBusy(true); setError(null)
      try {
        await signInWithGoogle(t.access_token)
        onSignedIn()
      } catch (e) {
        // No connection gets plain copy, never the browser's own wording.
        setError(e instanceof PatrolRequestError && e.offline
          ? 'No connection. Could not sign in.'
          : e instanceof Error ? e.message : 'Sign-in failed')
      } finally {
        setBusy(false)
      }
    },
    onError: () => setError('Google sign-in was cancelled or failed.'),
    onNonOAuthError: () => setError('Google sign-in failed (popup blocked or closed).'),
  })

  if (!GOOGLE_CONFIGURED) return null

  return (
    <div className="gate__staff">
      <button className="gate__signin" onClick={() => login()} disabled={busy}>
        <GoogleLogo /> {busy ? 'Signing in…' : 'Continue with Google'}
      </button>
      {error && <p className="gate__error">{error}</p>}
    </div>
  )
}

/** Same mark as ops's Google button — one icon to recognize across products. */
function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20H42v-.1H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 8 3l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5c-10.8 0-19.5 8.7-19.5 19.5S13.2 43.5 24 43.5c10 0 19-7.5 19-19.5 0-1.4-.1-2.7-.4-4z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 8 3l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5c-7.6 0-14.2 4.3-17.7 10.2z"/>
      <path fill="#4CAF50" d="M24 43.5c5.4 0 10.3-2 14-5.4l-6.5-5.5C29.7 34 27 35 24 35c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.2 16.3 43.5 24 43.5z"/>
      <path fill="#1976D2" d="M43.6 20H42v-.1H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.7l6.5 5.5c-.5.4 6.8-5 6.8-15.1 0-1.4-.1-2.7-.4-4z"/>
    </svg>
  )
}

export default function App() {
  const [key, setKey] = useState(() => claimCredential())
  // First open of a one-time setup link: trade it for this phone's own key
  // before anything else runs. 'used' = the server said the link is spent or
  // expired; 'failed' = we couldn't reach it (the link is kept, so retry works).
  const [setup, setSetup] = useState<'idle' | 'working' | 'used' | 'failed'>(
    () => (pendingSetupLink() ? 'working' : 'idle'))
  const [setupTry, setSetupTry] = useState(0)
  useEffect(() => {
    const invite = pendingSetupLink()
    if (!invite) return
    let live = true
    setSetup('working')
    enrollFromSetupLink(invite)
      .then(k => { if (live) { setKey(k); setAuthFailed(false); setSetup('idle') } })
      .catch((e: unknown) => { if (live) setSetup(e instanceof PatrolAuthError ? 'used' : 'failed') })
    return () => { live = false }
  }, [setupTry])
  // Bumped on sign-in / sign-out so the lots effect re-runs with the new
  // credential — the staff token lives in storage, not in this state.
  const [session, setSession] = useState(() => (readStaffSession() ? 1 : 0))
  const [partner, setPartner] = useState<string>('')
  const [zones, setZones] = useState<ZoneRef[] | null>(null)
  const [zoneId, setZoneId] = useState<string | null>(readLastLot)
  // Who the server answered as. Null until the lot list has loaded.
  const [kind, setKind] = useState<CallerKind | null>(null)
  // Staff and team members signed in with Google and have a session to end; a
  // partner link has nothing to sign out of.
  const signedInPerson = kind === 'staff' || kind === 'employee'
  const [authFailed, setAuthFailed] = useState(false)
  // A team member whose account does not include plate checks (server 403
  // `patrol_not_granted`). Their session is good, so it is kept.
  const [notGranted, setNotGranted] = useState(false)
  const [loadError, setLoadError] = useState<LoadFailure | null>(null)
  // Bumped to re-ask the server which lots this credential covers. The list is
  // NOT immutable for the life of a tab: an operator adds a lot to the partner
  // in the console (Client Admin → Enforcement partners) and the officer is
  // holding the same link. Before this existed the effect below ran once per
  // mount, and an officer with a remembered lot never remounts — so a newly
  // assigned lot did not appear until they fully reloaded the page, and
  // "Back to lots" re-rendered the same stale array it had held all shift.
  const [lotsNonce, setLotsNonce] = useState(0)

  const signedIn = !!key || session > 0

  // ── lots ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!signedIn) return
    const ac = new AbortController()
    setLoadError(null)
    fetchZones(ac.signal)
      .then(r => { setPartner(r.partner); setKind(callerKindOf(r)); setNotGranted(false); setZones(pickerEntries(r)) })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        if (e instanceof PatrolNotGrantedError) setNotGranted(true)
        else if (e instanceof PatrolAuthError) setAuthFailed(true)
        else setLoadError(failureOf(e))
      })
    return () => ac.abort()
  }, [signedIn, session, lotsNonce])

  // Re-ask when the phone comes back to this tab — but ONLY on the picker.
  //
  // Deliberately not while a roster is open. Refreshing the lot list can REMOVE
  // a lot as well as add one, and the effect below drops a remembered lot that
  // is no longer assigned; firing that mid-lookup would yank an officer off the
  // screen they are reading a plate from. On the picker there is nothing to
  // yank them out of, and the picker is where a missing lot is noticed.
  useEffect(() => {
    if (!signedIn || zoneId !== null) return
    const onVisible = () => { if (document.visibilityState === 'visible') setLotsNonce(n => n + 1) }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [signedIn, zoneId])

  const signOut = useCallback(() => {
    forgetStaffSession()
    setZones(null); setPartner(''); setKind(null); setAuthFailed(false); setNotGranted(false); setLoadError(null)
    setZoneId(null); writeLastLot(null)
    setSession(0)
  }, [])

  const signedInAgain = useCallback(() => {
    setAuthFailed(false); setNotGranted(false); setLoadError(null)
    setZones(null); setPartner(''); setKind(null)
    setSession(s => s + 1)
  }, [])

  // A remembered lot that is no longer assigned must not strand the officer on a
  // 403 screen — drop it and show the picker.
  useEffect(() => {
    if (zones && zoneId && !zones.some(z => z.id === zoneId)) {
      setZoneId(null); writeLastLot(null)
    }
  }, [zones, zoneId])

  // Re-ask for the lots without leaving the roster. The Back chevron that
  // triggers `unpick` is hidden on a phone, so the roster's own Change control is
  // the only lot gesture a phone officer has, and it has to be a refresh point
  // too. `zones` stays in state while this runs, so nothing flickers.
  const refreshLots = useCallback(() => setLotsNonce(n => n + 1), [])
  const pick = useCallback((id: string) => { setZoneId(id); writeLastLot(id) }, [])
  // Going back to the picker re-asks for the lots. This is the gesture an
  // officer makes when they have been told a new lot is theirs, so it is the
  // one place the list must not be a cache of whenever the tab was opened.
  const unpick = useCallback(() => {
    setZoneId(null); writeLastLot(null); setLotsNonce(n => n + 1)
  }, [])

  if (setup === 'working') {
    return <Gate title="Setting up this phone" body="One moment while we connect your patrol access." />
  }
  if (setup === 'failed') {
    return (
      <Gate title="Couldn't finish setup" body="Check your connection and try again. Your setup link still works.">
        <button className="gate__signin" onClick={() => setSetupTry(n => n + 1)}>Try again</button>
      </Gate>
    )
  }
  if (setup === 'used' && !key) {
    return (
      <Gate
        title="This setup link has already been used"
        body="Each link sets up one phone, once. Ask your Parkify contact for a new one."
      />
    )
  }
  if (!signedIn) {
    // A staff member whose 8h session ran out while the app was closed. Their
    // stored session was deleted on open, so without this they would see the
    // first-time partner-link screen, which is about a link they never had.
    if (hasLapsedStaffSession()) {
      return (
        <Gate title="Please sign in again" body="Your Parkify session has ended.">
          <StaffSignIn onSignedIn={signedInAgain} />
        </Gate>
      )
    }
    return (
      <Gate
        title="Open your patrol link"
        body="This tool opens from the link your Parkify contact sent you. Ask them to resend it if you no longer have it."
      >
        <StaffSignIn onSignedIn={signedInAgain} />
      </Gate>
    )
  }
  if (notGranted) {
    // Signed in, and the account is real, but it does not include patrol. Names
    // no zone and no count, as in the demo; signing in again would land here
    // again, so the way out is Sign out, not a sign-in button.
    return (
      <Gate title="Your account doesn’t include patrol" body="Ask your manager if you need it.">
        {/* Same spacing and divider as the sign-in button it replaces. */}
        <div className="gate__staff">
          <button className="gate__signin" onClick={signOut}>Sign out</button>
        </div>
      </Gate>
    )
  }
  if (authFailed) {
    // A partner's link is dead and only Parkify can revive it. A staff session
    // just needs signing in again, and the button is right there.
    return key
      ? <Gate title="This link is no longer active" body="Ask your Parkify contact for a new one." />
      : (
        <Gate title="Please sign in again" body="Your Parkify session has ended.">
          <StaffSignIn onSignedIn={signedInAgain} />
        </Gate>
      )
  }

  const zone = zones?.find(z => z.id === zoneId) ?? null

  return zone
    ? <Roster
        // A lot change ends this lookup context before the next roster loads.
        // Remount also aborts plate requests and drops old bay/closure answers.
        key={zone.id}
        zone={zone}
        zones={zones ?? []}
        partner={partner}
        kind={kind}
        onBack={unpick}
        onChangeZone={pick}
        onRefreshLots={refreshLots}
        onSignOut={signedInPerson ? signOut : null}
        onAuthFail={(e) => (e instanceof PatrolNotGrantedError ? setNotGranted(true) : setAuthFailed(true))}
      />
    : <Picker
        zones={zones} partner={partner} kind={kind} error={loadError} onPick={pick}
        onRetry={() => setLotsNonce(n => n + 1)}
        onSignOut={signedInPerson ? signOut : null}
      />
}

/** The mobile strip naming the operator the officer is working for. */
function PartnerBanner({ partner }: { partner: string }) {
  return (
    <div className="partner-banner" aria-label="Parking operator">
      <span className="partner-banner__dot" aria-hidden /><span className="partner-banner__name">{partner}</span>
    </div>
  )
}

// ─── screen 1: pick a lot ──────────────────────────────────────────────────

function Picker({ zones, partner, kind, error, onPick, onSignOut, onRetry }: {
  zones: ZoneRef[] | null
  partner: string
  /** Who the server answered as; null before the list loads. */
  kind: CallerKind | null
  error: LoadFailure | null
  onPick: (id: string) => void
  /** Non-null only for a signed-in person (staff or team member). A partner has nothing to sign out of. */
  onSignOut: (() => void) | null
  onRetry: () => void
}) {
  const [q, setQ] = useState('')
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!zones) return []
    if (!needle) return zones
    return zones.filter(z =>
      z.name.toLowerCase().includes(needle) || (z.address ?? '').toLowerCase().includes(needle))
  }, [zones, q])

  return (
    <div className="app">
      <header className="header">
        <Logo className="header__logo" color />
        {partner && (
          <div className="header__meta header__meta--picker">
            <div className="header__partner">{partner}</div>
          </div>
        )}
      </header>
      {/* The same operator strip the roster shows, so the partner is one purple
          strip on both screens on a phone. The header label above is the identity
          at wider widths (the strip is hidden there) and is hidden on a phone. */}
      {partner && <PartnerBanner partner={partner} />}
      <main className="viewport">
        <h1 className="h1">Parking check</h1>
        <p className="sub">
          {kind === 'staff' ? 'Choose a zone. You’re seeing every Parkify zone.' : 'Choose the zone you’re working.'}
        </p>

        {zones && zones.length > 6 && (
          <input
            className="search" type="search" inputMode="search"
            placeholder="Search zones…" value={q}
            onChange={e => setQ(e.target.value)} aria-label="Search zones"
          />
        )}

        {error && (
          <div className="note note--warn" role="alert">
            {error.offline ? 'No connection. Could not load your zones.' : 'Could not load your zones.'}{' '}
            <button type="button" className="note__action" onClick={onRetry}>Retry loading zones</button>
          </div>
        )}
        {!zones && !error && <p className="muted">Loading your zones…</p>}
        {zones && zones.length === 0 && (
          <p className="muted">
            {kind === 'employee'
              // A team member's zones come from their own operator, not from
              // Parkify, so the remedy names who can actually fix it.
              ? 'None of your zones are on patrol yet. Ask your manager to add one.'
              : 'No zones are assigned to your account yet. Your Parkify contact can add them.'}
          </p>
        )}
        {zones && zones.length > 0 && shown.length === 0 && (
          <p className="muted">No zones match “{q}”.</p>
        )}

        <ul className="lots">
          {shown.map(z => (
            <li key={z.id}>
              <button className={`lot${z.facility ? ' lot--facility' : ''}`} onClick={() => onPick(z.id)}>
                <span>
                  <span className="lot__name">{z.name}</span>
                  {z.facility && <FacilityZonesLine zones={z.facility.zones} />}
                  {z.address && <span className="lot__addr">{z.address}</span>}
                </span>
                <span className="lot__chev" aria-hidden>›</span>
              </button>
            </li>
          ))}
        </ul>

        {/* Staff only, and at the foot rather than in the header: a partner name
            stacked over a 44px button made the picker header 82px and moved the
            logo down 19.5px, then the roster snapped it back. The roster already
            keeps Sign out at its foot at every width, so the picker does too. */}
        {onSignOut && (
          <div className="roster__signout">
            <button type="button" className="roster__signout-btn" onClick={onSignOut}>Sign out</button>
          </div>
        )}
      </main>
    </div>
  )
}

// ─── screen 2: work the lot ────────────────────────────────────────────────

function Roster({ zone, zones, partner, kind, onBack, onChangeZone, onRefreshLots, onSignOut, onAuthFail }: {
  zone: ZoneRef
  zones: ZoneRef[]
  partner: string
  /** Who the server answered as. Staff see every zone; everyone else is assigned theirs. */
  kind: CallerKind | null
  onBack: () => void
  onChangeZone: (id: string) => void
  /** Re-fetch the lot assignment; called when Change opens. */
  onRefreshLots: () => void
  /** Non-null only for a signed-in person (staff or team member). A partner has no session to end. */
  onSignOut: (() => void) | null
  /** Leaves the roster. A PatrolNotGrantedError is told apart from a dead credential by the app. */
  onAuthFail: (e: PatrolAuthError) => void
}) {
  const [snap, setSnap] = useState<Snapshot | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<LoadFailure | null>(null)
  const [filter, setFilter] = useState<Filter>('all')
  const [q, setQ] = useState('')
  const [checked, setChecked] = useState<Vehicle | null>(null)
  // Near-misses for the plate currently on screen. Cleared alongside `checked`
  // so a stale list can never outlive the answer it belonged to.
  const [cands, setCands] = useState<Candidate[]>([])
  // The closure as resolved AT LOOKUP TIME, from the plate response. Distinct
  // from `snap.closure`, which is as of the last roster refresh — a closure that
  // began in between would otherwise be invisible while the officer is standing
  // at the car. Cleared alongside `checked` so it can never outlive its answer.
  //
  // THREE states, deliberately: `undefined` is "no lookup has answered yet, fall
  // back to the snapshot"; `null` is "a lookup answered and the lot is OPEN"; an
  // object is "a lookup answered and it is closed". The Worker always sends the
  // field (null when open); a response that omits it (an older Worker) stays
  // `undefined`. Collapsing the first two is the bug this shape prevents: a
  // closure that ENDED since the last roster refresh would keep painting the
  // snapshot's blue banner over a normal verdict.
  const [checkedClosure, setCheckedClosure] = useState<ZoneClosure | null | undefined>(undefined)
  // The lot's policy as the plate response reported it: the operator's arrival
  // grace and the lot timezone. A field left `undefined` means the response did
  // not carry it (an older Worker), and the roster's copy is used instead, so a
  // lookup typed before the roster has loaded still words both. Cleared
  // alongside `checked` so it can never outlive its answer.
  const [checkedPolicy, setCheckedPolicy] = useState<LookupPolicy>({})
  /**
   * When the answer currently on screen was obtained, and — if the officer
   * arrived at it by tapping a near-miss — what they originally entered.
   *
   * The pairing is the point. "We booted MNP2210" and "we booted MNP2210, which
   * the officer reached from MNP2216 by choosing it off a similar-plates list"
   * are different stories in a wrongful-tow dispute, and only the second one
   * explains itself weeks later. Once the result re-renders for the candidate
   * the typed plate is otherwise gone, because `checked` has been replaced.
   */
  const [provenance, setProvenance] = useState<Provenance | null>(null)
  const [enteredAs, setEnteredAs] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)
  const [checkingPlate, setCheckingPlate] = useState('')
  const [lookupError, setLookupError] = useState<{ plate: string; offline: boolean } | null>(null)
  const plateAc = useRef<AbortController | null>(null)
  // Focus targets. Opening a result unmounts the roster row that held focus, and
  // the Check button disables itself, so without these keyboard and screen reader
  // focus falls to the document body and the officer starts again from the top.
  const resultRef = useRef<HTMLDivElement | null>(null)
  const lotHeadingRef = useRef<HTMLHeadingElement | null>(null)
  // Bay lookup — the SECOND question on a reserved-bay lot (RESERVED-BAYS-SPEC
  // §3), asked independently of the plate. `bayResult` is cleared with the
  // input so a stale holder can never sit next to a different bay number.
  const [bayQ, setBayQ] = useState('')
  const [bayResult, setBayResult] = useState<BayResult | null>(null)
  const [bayChecking, setBayChecking] = useState(false)
  const [showZonePicker, setShowZonePicker] = useState(false)
  const [zoneQuery, setZoneQuery] = useState('')
  const zoneListRef = useRef<HTMLDivElement | null>(null)
  const plateInputRef = useRef<HTMLInputElement | null>(null)
  const visibleZones = useMemo(() => {
    const needle = zoneQuery.trim().toLowerCase()
    return needle
      ? zones.filter(z => z.name.toLowerCase().includes(needle) || (z.address ?? '').toLowerCase().includes(needle))
      : zones
  }, [zones, zoneQuery])
  // A long list opens already showing the zone the officer is working: with 25 zones
  // the current one sat below the fold of the capped list and the popover looked as if
  // nothing was selected. The list's own scroll is set, not the page's, so opening it
  // never moves the screen under the officer's thumb.
  useEffect(() => {
    if (!showZonePicker) return
    const list = zoneListRef.current
    const current = list?.querySelector<HTMLElement>('[aria-current="true"]')
    if (!list || !current) return
    list.scrollTop = Math.max(0, current.offsetTop - (list.clientHeight - current.offsetHeight) / 2)
  }, [showZonePicker])
  const ac = useRef<AbortController | null>(null)
  // The clock the roster's age is measured against — see the age block below.
  // Set in the same breath as the snapshot so the two can never disagree.
  const [nowMs, setNowMs] = useState(() => Date.now())

  const load = useCallback(() => {
    ac.current?.abort()
    const next = new AbortController()
    ac.current = next
    setBusy(true); setError(null)
    fetchRoster(zone.id, next.signal)
      .then(s => { setSnap(s); setNowMs(Date.now()); setBusy(false) })
      .catch((e: unknown) => {
        if (next.signal.aborted) return
        setBusy(false)
        if (e instanceof PatrolAuthError) onAuthFail(e)
        else setError(failureOf(e))
      })
  }, [zone.id, onAuthFail])

  useEffect(() => { load(); return () => ac.current?.abort() }, [load])

  // The roster is time-windowed, while a deliberate plate lookup is not. Keep
  // the two questions separate: typing narrows the visible roster and shows a
  // preflight when there is no match; Check is the explicit server lookup.
  const normalized = q.trim().toUpperCase().replace(/[^A-Z0-9]/g, '')
  const matches = useMemo(() => {
    const roster = snap?.vehicles ?? []
    return normalized ? roster.filter(v => rosterMatch(normalized, v.plate)) : roster
  }, [snap, normalized])
  const exactMatch = useMemo(
    () => normalized ? (snap?.vehicles ?? []).find(v => v.plate === normalized) ?? null : null,
    [snap, normalized],
  )

  // Only a changed plate query closes the open result. The roster's SIZE is not
  // a reason: a result opened by tapping a row has an empty query, so `matches`
  // is the whole roster and any Refresh that added or dropped a car would
  // silently close the card the officer was reading.
  useEffect(() => {
    plateAc.current?.abort()
    setChecked(null); setCands([]); setCheckedClosure(undefined); setCheckedPolicy({})
    setProvenance(null); setEnteredAs(null); setChecking(false)
    setLookupError(null)
    return () => plateAc.current?.abort()
  }, [normalized])

  const requestPlateLookup = useCallback((plate = normalized) => {
    if (plate.length < 2 || checking) return
    plateAc.current?.abort()
    const next = new AbortController()
    plateAc.current = next
    setChecking(true); setCheckingPlate(plate); setLookupError(null)
    // Check with a result already open asks the server again for the plate in the
    // field. When the card on screen IS that plate it stays and its receipt line
    // reads "Rechecking <plate>..." until the new answer lands and stamps a new
    // time. A card for a DIFFERENT plate (a near-miss the officer picked) would
    // sit under a receipt for the wrong car, so it is cleared first.
    if (checked) {
      if (checked.plate === plate) setProvenance(null)
      else { setChecked(null); setCands([]); setCheckedClosure(undefined); setCheckedPolicy({}); setProvenance(null) }
      setEnteredAs(null)
    }
    checkPlate(zone.id, plate, next.signal)
      .then(r => {
        if (next.signal.aborted) return
        setChecked(r.vehicle); setCands(r.candidates ?? [])
        setCheckedClosure(r.closure); setCheckedPolicy(policyOf(r)); setChecking(false)
        // Stamped when the ANSWER lands, not when the query was typed — the
        // officer screenshots what they were shown, at the moment shown.
        setProvenance({ kind: 'checked', at: new Date() })
      })
      .catch((e: unknown) => {
        // An old rejection must not clear the spinner for a newer lookup.
        if (next.signal.aborted || plateAc.current !== next) return
        setChecking(false)
        if (e instanceof PatrolAuthError) onAuthFail(e)
        else {
          // A failed re-check cannot leave the old answer looking confirmed.
          setChecked(null); setCands([]); setCheckedClosure(undefined); setCheckedPolicy({})
          setProvenance(null); setEnteredAs(null)
          setLookupError({ plate, offline: failureOf(e).offline })
        }
      })
  }, [normalized, checked, checking, zone.id, onAuthFail])

  const bayLabel = bayQ.trim().replace(/\s+/g, ' ').toUpperCase()
  useEffect(() => {
    setBayResult(null)
    if (!bayLabel) { setBayChecking(false); return }
    const next = new AbortController()
    setBayChecking(false)
    const t = setTimeout(() => {
      setBayChecking(true)
      checkBay(zone.id, bayLabel, next.signal)
        .then(r => { if (!next.signal.aborted) { setBayResult(r); setBayChecking(false) } })
        .catch((e: unknown) => {
          if (next.signal.aborted) return
          setBayChecking(false)
          if (e instanceof PatrolAuthError) onAuthFail(e)
          // Any other failure reads as unknown — never as free.
          else setBayResult({ zoneId: zone.id, label: bayLabel, status: 'unknown', section: null, reservedFor: null, headline: 'Bay status unknown. We could not check this bay right now.' })
        })
    }, 350)
    return () => { clearTimeout(t); next.abort() }
  }, [bayLabel, zone.id, onAuthFail])

  const shown = useMemo(() => {
    // Typing searches the whole roster. The chips are hidden while a query is
    // set (and at phone width entirely), so a chip chosen earlier must not keep
    // hiding rows the summary ("N of M match") is already counting.
    if (normalized) return matches
    if (filter === 'live') {
      return matches.filter(v => v.verdict === 'authorized' || v.verdict === 'grace')
    }
    // `closed` is excluded from both chips, matching `counts` — see its doc
    // comment in api.ts. A closure withdrew the claim that this row is a
    // violation, so "Needs a look" must not still list it; the row stays
    // visible under "All" instead.
    if (filter === 'attention') {
      return matches.filter(
        v => v.verdict !== 'authorized' && v.verdict !== 'grace' && v.verdict !== 'closed',
      )
    }
    return matches
  }, [matches, filter, normalized])

  // Clears BOTH ways a result can be open — a tap (no query) and a search (query
  // set). Resetting q alone wouldn't close a tapped result; clearing `checked`
  // alone would leave the roster filtered by the searched plate. Handling both
  // here keeps "clear to browse again" one action.
  const clearResult = useCallback(() => {
    plateAc.current?.abort()
    setChecked(null); setCands([]); setCheckedClosure(undefined); setCheckedPolicy({}); setQ('')
    setProvenance(null); setEnteredAs(null)
    setLookupError(null)
  }, [])

  /**
   * Open the result for one vehicle, and go get its near-misses.
   *
   * The fetch is the load-bearing half. Typing a plate already on the roster
   * does not query the server on its own, so a car sitting right there reading
   * "Expired" would show no candidates after a row tap without this lookup.
   * That is precisely the case the feature exists for: MNP2216 is a real record,
   * and the authorized MNP2210 one character away is the reason not to boot it.
   *
   * Authorized cards still open the detail view, but skip the call: there is
   * nothing to second-guess and the roster already has the complete answer.
   *
   * WHAT THE CARD MAY CLAIM follows from that. The line under the answer is the
   * screenshot-able receipt, so it says "Checked <time>" only for an answer a
   * lookup actually returned. A roster answer (authorized, no call) says
   * "Roster as of <snapshot time>", and a negative row, which shows the
   * snapshot's card until the recheck lands, carries no stamp at all and says
   * it is rechecking.
   */
  const snapAsOf = snap?.asOf ?? null
  const openResult = useCallback((v: Vehicle, cameFrom?: string) => {
    plateAc.current?.abort()
    setChecking(false)
    setLookupError(null)
    setChecked(v)
    setCands([])
    setCheckedPolicy({})
    // Only a NEAR-MISS pick carries an origin. A row tapped straight off the
    // roster was not reached from anything, so recording one would invent a
    // correction the officer never made.
    setEnteredAs(cameFrom ?? null)
    // A row tapped off the roster starts from the snapshot's copy (undefined
    // falls back to it), then upgrades to the lookup-time answer, open or
    // closed, when the re-check lands below.
    setCheckedClosure(undefined)
    // A facility card always asks the server: the roster row knows the zone that
    // authorizes, but only a lookup carries every zone's own verdict for the card.
    if (v.verdict === 'authorized' && !zone.facility) {
      // A near-miss candidate arrived inside a lookup response, so the stamp
      // that response earned still stands for it. A tapped roster row has no
      // lookup behind it: it is the snapshot's answer, dated by the snapshot.
      const asOf = snapAsOf ? new Date(snapAsOf) : new Date()
      setProvenance(prev => cameFrom && prev?.kind === 'checked' ? prev : { kind: 'roster', at: asOf })
      return
    }
    // No stamp until a lookup answers. The negative card on screen meanwhile is
    // the snapshot's, and `provenance === null` renders as "Rechecking".
    setProvenance(null)
    const ac = new AbortController()
    plateAc.current = ac
    checkPlate(zone.id, v.plate, ac.signal)
      .then(r => {
        if (ac.signal.aborted) return
        setChecked(r.vehicle); setCands(r.candidates ?? []); setCheckedClosure(r.closure)
        setCheckedPolicy(policyOf(r))
        setProvenance({ kind: 'checked', at: new Date() })
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        if (e instanceof PatrolAuthError) onAuthFail(e)
        else {
          // A failed re-check cannot leave an old negative answer looking confirmed.
          setChecked(null); setCands([]); setCheckedClosure(undefined); setCheckedPolicy({})
          setProvenance(null); setEnteredAs(null)
          setLookupError({ plate: v.plate, offline: failureOf(e).offline })
        }
      })
  }, [zone.id, zone.facility, onAuthFail, snapAsOf])

  // Move focus to the answer when one opens (or the plate changes under a
  // near-miss pick, whose button unmounts). Keyed on the plate, not on `checked`:
  // the server re-check replaces `checked` with the same car and must not yank
  // focus a second time.
  const checkedPlate = checked?.plate ?? null
  useEffect(() => {
    if (checkedPlate) resultRef.current?.focus()
  }, [checkedPlate])

  // Back to the roster. Focus lands on the lot heading, NOT the plate input: a
  // programmatic focus() inside a tap raises the on-screen keyboard over the very
  // list the officer just came back to read. The heading is always mounted, takes
  // no keyboard, and puts a screen reader at the top of the roster.
  const backToRoster = useCallback(() => {
    clearResult()
    lotHeadingRef.current?.focus()
  }, [clearResult])

  const checkExact = useCallback(() => {
    if (exactMatch) openResult(exactMatch)
  }, [exactMatch, openResult])

  const checkCurrent = useCallback(() => {
    // With a result open, Check is a re-check: it goes to the server rather than
    // re-opening the roster's copy, so the answer and its time are fresh.
    if (exactMatch && !checked) checkExact()
    else requestPlateLookup()
  }, [exactMatch, checked, checkExact, requestPlateLookup])
  const canCheck = (normalized.length >= 2 || !!exactMatch) && !checking

  // The zone closure banner (see the note where it renders). Prefers the lookup-time
  // closure over the snapshot's, so a closure that began since the last refresh
  // shows up while the officer is still at the car, and one that ENDED since then
  // stops showing (a lookup that answers "open" is an answer, not an absence).
  // Every string is the server's, verbatim: the reopen time is worded in the LOT's
  // timezone.
  //
  // A FACILITY has one banner only when every zone in it is closed; otherwise each
  // closed zone gets its own line naming it, from the lookup when there is one
  // and the roster otherwise. One zone's closure is never shown as the facility's.
  const facilityStates: FacilityZoneState[] | null = zone.facility ? (checkedPolicy.zones ?? snap?.zones ?? null) : null
  const closure = facilityStates
    ? (facilityStates.length > 0 && facilityStates.every(s => !!s.closure) ? facilityStates[0].closure : null)
    : checkedClosure !== undefined ? checkedClosure : snap?.closure ?? null
  const zoneClosures = facilityStates && !closure ? facilityStates.filter(s => !!s.closure) : []

  // Arrival grace is the lot's "time to pay after arriving", and a closed lot
  // that is still enforcing sold nobody parking: a car there has no window to pay
  // in, only an authorization or not. The server leaves verdicts untouched in that
  // state (so an unauthorized plate stays red), which is exactly why the
  // clause has to come off here: "Ran out 3 min ago. This lot allows 15 min to
  // pay after arriving." under an enforcement-is-ON banner contradicts it.
  const graceApplies = closure?.state !== 'enforcing'

  // The policy the result card words itself with: the lookup's own copy when the
  // plate response carried it, else the roster's.
  const resultArrivalGrace = !graceApplies ? null
    : checkedPolicy.arrivalGraceMinutes !== undefined
      ? checkedPolicy.arrivalGraceMinutes
      : snap?.arrivalGraceMinutes ?? null
  const resultTimezone = checkedPolicy.timezone !== undefined
    ? checkedPolicy.timezone
    : snap?.timezone ?? null

  // How old the roster on screen actually is, ticking on its own.
  //
  // The screen used to state only the absolute time it was fetched ("As of 3:42
  // PM"), which is a number the officer has to diff against their own watch
  // while standing behind a car. Booting someone who paid ninety seconds ago is
  // the expensive mistake this tool exists to prevent, so the age is stated
  // outright and it keeps counting: a tab left open on a dashboard mount reads
  // as fresh forever otherwise, because nothing re-renders.
  //
  // DERIVED DURING RENDER, deliberately. This used to be state written by an
  // effect, and effects run AFTER paint — so the roster painted one frame of
  // "Updated just now" with no staleness warning, however old the snapshot
  // actually was, and only then corrected itself. One frame is enough to read a
  // plate off a list you have just been told is current. On the one screen whose
  // job is to never overstate how fresh it is, the age has to be right in the
  // very first frame that shows the rows, so it is computed from the snapshot
  // rather than chased after it. The interval only moves the clock forward.
  //
  // The interval alone is not enough on a phone. A locked or backgrounded tab has
  // its timers throttled or frozen, so an officer who unlocks after twenty minutes
  // would read the last rendered age ("Live · updated just now") until the next
  // tick, which is exactly the overstatement the paragraph above exists to
  // prevent. So the clock is also re-read the moment the page comes back, on
  // every signal a browser gives for that. This only MOVES THE CLOCK: it never
  // refreshes the roster. The stale policy is to offer the refresh, not take it.
  useEffect(() => {
    if (!snap) return
    const tick = () => setNowMs(Date.now())
    const onVisible = () => { if (document.visibilityState === 'visible') tick() }
    const id = setInterval(tick, 5_000)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('pageshow', tick)
    window.addEventListener('focus', tick)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('pageshow', tick)
      window.removeEventListener('focus', tick)
    }
  }, [snap])
  const ageSec = snap ? Math.max(0, Math.round((nowMs - Date.parse(snap.asOf)) / 1000)) : 0
  const stale = ageSec >= STALE_AFTER_SEC
  const hardStale = ageSec >= HARD_STALE_AFTER_SEC
  // A refresh failed while a roster is on screen: the list is the last good one.
  const refreshFailed = !!(error && snap)

  // Worker and Pages deploy independently. If this bundle meets the previous
  // Worker (which has neither new key), derive the presentation counts from the
  // rows already in the same snapshot. The new Worker retains legacy
  // `attention`, so only use the new pair when BOTH keys are present.
  const counts = useMemo(() => {
    if (!snap) return { all: 0, live: 0, attention: 0 }
    if (typeof snap.counts.live === 'number' && typeof snap.counts.needsAction === 'number') {
      return { all: snap.counts.all, live: snap.counts.live, attention: snap.counts.needsAction }
    }
    return {
      all: snap.counts.all,
      live: snap.vehicles.filter(v => v.verdict === 'authorized' || v.verdict === 'grace').length,
      attention: snap.vehicles.filter(
        v => v.verdict !== 'authorized' && v.verdict !== 'grace' && v.verdict !== 'closed',
      ).length,
    }
  }, [snap])

  // Austin's patrol surface puts the three officer-facing buckets directly
  // under the lot name. Keep the existing filter chips as a secondary control,
  // but make the first glance answer the same question as the demo: how many
  // cars are safe to leave alone, waiting for verification, or expired?
  // Every segment is derived from the same rows, so together they always sum to
  // the roster size (the chips hide on a phone, which leaves this line as the
  // only count). Unconfirmed and Lot closed rows are in neither `live` nor
  // "pending" or "expired", so without their own segments they would show in the
  // list but in no count. Wording comes from the row labels.
  const zoneSummary = useMemo(() => {
    const vehicles = snap?.vehicles ?? []
    const count = (verdict: Verdict) => vehicles.filter(v => v.verdict === verdict).length
    const label = (verdict: Verdict) => labelFor({ verdict } as Vehicle).toLowerCase()
    return {
      live: vehicles.filter(v => v.verdict === 'authorized' || v.verdict === 'grace').length,
      pending: count('pending'),
      unverified: count('unverified'),
      expired: count('expired'),
      closed: count('closed'),
      noRecord: count('no_record'),
      unverifiedLabel: label('unverified'),
      // An Inactive lot's rows say "not taking payment", a closed lot's "lot
      // closed": take the word from the rows themselves, never from the bare verdict.
      closedLabel: (() => {
        const first = vehicles.find(v => v.verdict === 'closed')
        return first ? labelFor(first).toLowerCase() : label('closed')
      })(),
      noRecordLabel: label('no_record'),
    }
  }, [snap])

  // What a screen reader is told as the state changes. Rendered into live regions
  // that are ALWAYS mounted: a region inserted into the DOM with its text already
  // in it is not reliably announced, so the answer an officer acts on (the result
  // card is inserted exactly that way) could be silent. Writing into a region
  // that was already there is what gets read. The card's words are used as the
  // card renders them (same grace figure and lot timezone), and while a tapped
  // negative row is still being re-checked the region says so, then re-speaks
  // when the re-check's answer lands.
  const announcement = checking ? `Checking ${checkingPlate} directly`
    : checked ? `${headlineFor(checked)}. ${checked.plate}. ${guidanceFor(withLiveMinutes(checked, nowMs), resultArrivalGrace, resultTimezone)}${provenance ? '' : ` Rechecking ${checked.plate}.`}`
    : bayChecking ? `Checking bay ${bayLabel}`
    : ''

  const closureNote = (belowField: boolean) => closure ? (
    <div className={`note note--${closure.suppresses ? 'info' : 'warn'} note--closure${belowField ? ' note--after-field' : ''}`} role="status">
      <strong className="note__title">{closure.headline}</strong>{' '}
      <span className="note__body">{closure.body}</span>
    </div>
  ) : zoneClosures.map(s => s.closure && (
    <div key={s.zoneId} className={`note note--${s.closure.suppresses ? 'info' : 'warn'} note--closure${belowField ? ' note--after-field' : ''}`} role="status">
      <strong className="note__title">{s.zoneName}: {s.closure.headline}</strong>{' '}
      <span className="note__body">{s.closure.body}</span>
    </div>
  ))

  return (
    <div className="app">
      <header className="header">
        <button className="back" onClick={onBack} aria-label="Back to zones">‹</button>
        <Logo className="header__logo" color />
        <div className="header__meta">
          <div className="header__count">
            {snap ? `${counts.all} ${counts.all === 1 ? 'vehicle' : 'vehicles'}` : '—'}
          </div>
          {partner && <div className="header__partner">{partner}</div>}
        </div>
      </header>
      {partner && <PartnerBanner partner={partner} />}

      <main className="viewport">
        <div className="sr-only" aria-live="polite" aria-atomic="true">{announcement}</div>
        {/* Its own region, and its text is constant while the data stays stale.
            The visible freshness line counts up every minute; as a live region it
            would re-announce assertively each time the number changed. This one
            speaks once, when the roster crosses into stale. */}
        <div className="sr-only" role="alert">{snap && stale ? 'This roster is out of date. Refresh it before enforcing.' : ''}</div>
        <div className="lotbar">
          <h1 className="lotbar__name" tabIndex={-1} ref={lotHeadingRef}><span className="lotbar__prefix">{zone.facility ? '' : 'Zone '}</span>{zone.name}</h1>
          <button
            type="button"
            className="change-lot"
            aria-expanded={showZonePicker}
            aria-controls="zone-switcher"
            onClick={() => {
              setZoneQuery('')
              // Opening is the moment to ask again: an operator may have added a
              // lot since the tab loaded, and this officer has no other way to
              // find out short of reloading the page.
              if (!showZonePicker) onRefreshLots()
              setShowZonePicker(v => !v)
            }}
          >
            Change
          </button>
        </div>
        {zone.address && <p className="sub sub--addr">{zone.address}</p>}
        {zone.facility && <p className="sub sub--addr sub--zones"><FacilityZonesLine zones={zone.facility.zones} /></p>}

        {/* A failed refresh keeps the last good roster on screen, so the line must
            stop saying "Live" while the failure note is up: it takes the stale
            tone and says what the list is. */}
        <div className="statusbar">
          {snap && (
            <p className={`freshness freshness--${hardStale ? 'stale' : stale || refreshFailed ? 'aging' : 'live'}`}>
              <span className="freshness__dot" aria-hidden>●</span>
              {refreshFailed ? `Last good roster, updated ${ageLabel(ageSec)}`
                : stale ? `Data ${ageLabel(ageSec)}. Refresh before enforcing.` : `Live · updated ${ageLabel(ageSec)}`}
            </p>
          )}
          <button className="refresh" onClick={load} disabled={busy}>
            {busy ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {/* Plain copy, never the raw error (it can read "Failed to fetch"), and it
            always offers the way out. With a roster on screen it sits directly
            under the Refresh that failed, in the same slot as the stale banner, and
            says the list is the last good one and how old it is. A first load that
            fails has no roster and keeps the note under the plate field. */}
        {error && snap && (
          <div className="note note--warn" role="alert">
            {error.offline ? 'No connection. ' : ''}
            {`Could not refresh. Showing the last good roster, updated ${ageLabel(ageSec)}.`}
            {' '}
            <button type="button" className="note__action" onClick={load} disabled={busy}>
              {busy ? 'Trying…' : 'Try again'}
            </button>
          </div>
        )}

        {/* Above the roster, not in the footer: if this matters at all it
            matters BEFORE the officer reads a verdict off the list. */}
        {snap && stale && (
          <div className="note note--warn">
            <strong>Last updated {ageLabel(ageSec)}.</strong> Someone may have paid since.
            {' '}
            <button type="button" className="note__action" onClick={load} disabled={busy}>
              {busy ? 'Refreshing…' : 'Refresh before acting'}
            </button>
          </div>
        )}

        {/* Opens UNDER the freshness row, as in the demo: the chip that opened it,
            the address and the Live or Refresh line stay where they were, and the
            card starts 14px below them. It used to sit between the address and that
            row, flush against the address text and pushing Live and Refresh a card's
            height away from the control that was just tapped. */}
        {showZonePicker && (
          <div id="zone-switcher" className="zone-switcher" aria-label={kind === 'staff' ? 'Available zones' : 'Assigned zones'}>
            {zones.length > 3 && (
              <input
                className="zone-switcher__search"
                type="search"
                inputMode="search"
                placeholder="Search zones…"
                aria-label="Search zones"
                value={zoneQuery}
                onChange={e => setZoneQuery(e.target.value)}
              />
            )}
            <div className="zone-switcher__list" ref={zoneListRef}>
              {visibleZones.map(z => (
                <button
                  type="button"
                  key={z.id}
                  className={`zone-switcher__option${z.id === zone.id ? ' zone-switcher__option--selected' : ''}`}
                  // The current zone is marked by its wash, as in the demo; the wash
                  // is not available to a screen reader, so the state is too.
                  aria-current={z.id === zone.id ? 'true' : undefined}
                  onClick={() => {
                    setZoneQuery('')
                    setShowZonePicker(false)
                    if (z.id !== zone.id) onChangeZone(z.id)
                  }}
                >
                  <span>
                    <strong>{z.name}</strong>
                    {z.address && <small>{z.address}</small>}
                  </span>
                </button>
              ))}
              {visibleZones.length === 0 && (
                <p className="zone-switcher__empty" role="status">No zones match “{zoneQuery}”.</p>
              )}
            </div>
            <p className="zone-switcher__hint">
              {(() => { const zoneCount = zones.filter(z => !z.facility).length; return kind === 'staff'
                ? `${zoneCount} ${zoneCount === 1 ? 'zone' : 'zones'} available.`
                : `${zoneCount === 1 ? 'You’re assigned 1 zone.' : `You’re assigned ${zoneCount} zones.`} ${kind === 'employee' ? 'Ask your manager to add more.' : 'Ask your Parkify contact to add more.'}` })()}
            </p>
          </div>
        )}

        {/* A zone-level fact, snapshot-driven, like the hybrid-coverage banner it
            replaced: something true of the whole LOT that changes how every verdict
            below should be read.

            Visible BEFORE anything is typed, which is the point: an officer
            arriving at a suspended lot should know on sight. In the demo's order it
            sits straight under the plate field (field, banner, Back, result,
            roster), which keeps the field the officer types in where it always is.
            On a lot with reserved bays the bay card would push a banner placed
            below the field down towards the keyboard, so there it stays above the
            bay input, the one slot that is never covered.

            Colour carries the enforcement asymmetry, because it is the part read
            at arm's length without reading the words: blue when enforcement is
            genuinely suspended, amber when the lot is closed and enforcement
            stays ON. Losing that distinction is how a reserved-lot client's
            enforcement silently stops. */}
        {zone.reservedBays && closureNote(false)}

        {/* Bay first, plate second — the order an officer walks it: they are
            standing at a numbered space before they read the plate in it. The
            answer is about the SPACE, so it renders even when no plate is typed,
            and it never changes the plate verdict below. */}
        {zone.reservedBays && <>
          <input
            className="search search--bay" type="search" inputMode="text" autoCapitalize="characters"
            autoCorrect="off" spellCheck={false}
            // Next, because the bay is the first of the two questions: the key takes
            // the officer to the plate field, and Enter does that (it did nothing).
            enterKeyHint="next"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                plateInputRef.current?.focus()
              }
            }}
            placeholder="Bay number (reserved lots)" value={bayQ}
            onChange={e => setBayQ(e.target.value)} aria-label="Bay number"
          />
          {bayChecking && <p className="lookup-status">Checking bay {bayLabel}…</p>}
          {bayResult && <BayResultView r={bayResult} typedPlate={normalized} plateFinal={checking || !!checked || !!lookupError} />}
        </>}

        <div className="plate-label">CHECK A PLATE</div>
        <div className="plate-search">
          <input
            ref={plateInputRef}
            className="search plate-search__input" type="search" inputMode="text" autoCapitalize="characters"
            autoCorrect="off" spellCheck={false}
            // The key says what it does: Enter runs the same check as the button.
            enterKeyHint="search"
            placeholder="ABC1234" value={q}
            // The field shows the plate that will be checked: uppercase A to Z
            // and 0 to 9 only, also for pasted or dictated text (demo normPlate).
            onChange={e => setQ(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            onKeyDown={e => {
              if (e.key === 'Enter' && canCheck) {
                e.preventDefault()
                checkCurrent()
              }
            }}
            aria-label="Search license plate" aria-keyshortcuts="Enter"
          />
          <button
            type="button"
            className="search-check"
            onClick={checkCurrent}
            disabled={!canCheck}
            aria-label="Check plate"
          >
            Check
          </button>
        </div>

        {!zone.reservedBays && closureNote(true)}

        {/* One job per state, as in the demo: while a result is open the roster
            header and its count would only restate the row the answer came from
            ("0 of 6 match" above a No record card), so they step aside. */}
        {snap && !checked && (
          <p className="zone-summary" aria-label={zone.facility ? 'Vehicles in this facility' : 'Vehicles in this zone'}>
            {/* The label never wraps ("IN THIS / ZONE"); the counts sit right-aligned
                beside it and break between segments, never inside one ("1 no /
                record"). The text is unchanged: segments joined by " · ". */}
            <span className="zone-summary__label">{zone.facility ? 'IN THIS FACILITY' : 'IN THIS ZONE'}</span>{' '}
            <span className="zone-summary__counts">
              {normalized
                ? <span className="zone-summary__seg">{matches.length} of {snap.vehicles.length} match</span>
                : [
                  `${zoneSummary.live} live`,
                  zoneSummary.pending > 0 ? `${zoneSummary.pending} pending` : null,
                  zoneSummary.unverified > 0 ? `${zoneSummary.unverified} ${zoneSummary.unverifiedLabel}` : null,
                  zoneSummary.expired > 0 ? `${zoneSummary.expired} expired` : null,
                  zoneSummary.noRecord > 0 ? `${zoneSummary.noRecord} ${zoneSummary.noRecordLabel}` : null,
                  zoneSummary.closed > 0 ? `${zoneSummary.closed} ${zoneSummary.closedLabel}` : null,
                ].filter((seg): seg is string => !!seg).map((seg, i) => (
                  <span key={seg}>{i > 0 ? ' · ' : ''}<span className="zone-summary__seg">{seg}</span></span>
                ))}
            </span>
          </p>
        )}

        {/* US plates run 2–8 characters, so anything longer is more likely a
            permit or VIN number than a misread plate. Just a hint — the lookup
            below still runs, since a handful of states do issue longer plates. */}
        {normalized.length > 8 && (
          <div className={`note note--warn${snap ? '' : ' note--after-field'}`}>
            {normalized.length} characters. US plates are 2–8. This looks like a
            permit or VIN number, not a plate.
          </div>
        )}

        {/* The server's own answer, only after the officer explicitly chooses
            Check — including when another roster plate contains these chars. */}
        {checking && !checked && (
          <p className="lookup-status">
            Checking {checkingPlate} directly…
          </p>
        )}
        {lookupError && (
          <div className={`note note--warn${snap ? '' : ' note--after-field'}`} role="alert">
            {lookupError.offline ? 'No connection. ' : ''}Could not check {lookupError.plate}. Try again before acting.
            {' '}
            <button type="button" className="note__action" onClick={() => requestPlateLookup(lookupError.plate)} disabled={checking}>
              Retry plate check
            </button>
          </div>
        )}
        {checked && (
          <>
            <button type="button" className="result__clear" onClick={backToRoster}>
              <svg className="result__chevron" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden><path d="M9 2 4 7l5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>{zone.facility ? 'Back to the facility list' : 'Back to the zone list'}
            </button>
            <PlateResult
              innerRef={resultRef}
              v={withLiveMinutes(checked, nowMs)}
              arrivalGrace={resultArrivalGrace}
              timezone={resultTimezone}
              nowMs={nowMs}
              provenance={provenance}
              zoneName={zone.name}
              enteredAs={enteredAs}
            />
            {checkedPolicy.zones && checkedPolicy.zones.length > 1 && (
              <FacilityVerdicts zones={checkedPolicy.zones} plate={checked.plate} />
            )}
            {/* Not for grace. The card says "Not enforceable", so a panel under it
                reading "act on it instead" would contradict it. The server already
                sends none; this covers a Pages build that is ahead of the Worker. */}
            {cands.length > 0 && checked.verdict !== 'grace' && (
              <NearMisses
                typed={checked.plate}
                verdict={checked.verdict}
                candidates={cands}
                // The plate the officer was looking at when they chose the
                // candidate — which is the first thing they typed, if this is
                // the second correction in a row.
                onPick={c => openResult(c, enteredAs ?? checked.plate)}
              />
            )}
          </>
        )}

        {!normalized && !checked && (
          <div className="chips">
            <button className="chip" aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
              All ({counts.all})
            </button>
            <button className="chip chip--ok" aria-pressed={filter === 'live'} onClick={() => setFilter('live')}>
              Live ({counts.live})
            </button>
            <button className="chip chip--bad" aria-pressed={filter === 'attention'} onClick={() => setFilter('attention')}>
              Needs a look ({counts.attention})
            </button>
          </div>
        )}

        {/* The first load failed, so there is no roster and no Refresh to sit next
            to: plain copy with the way out, the same shape as the plate-check
            failure. A failed refresh is rendered up by the statusbar. */}
        {error && !snap && (
          <div className="note note--warn note--after-field" role="alert">
            {error.offline ? 'No connection. ' : ''}
            Could not load this zone.
            {' '}
            <button type="button" className="note__action" onClick={load} disabled={busy}>
              {busy ? 'Trying…' : 'Try again'}
            </button>
          </div>
        )}
        {!snap && !error && <p className="muted">Loading…</p>}

        {snap && shown.length === 0 && !checked && !checking && !lookupError && normalized.length >= 2 ? (
          <>
            <div className="lookup-empty" role="status">
              <strong>No match in this {zone.facility ? 'facility' : 'zone'}</strong>
              <p>No plate in this {zone.facility ? 'facility' : 'zone'} contains {normalized}. Tap Check anyway. It also catches swapped characters and misreads this list can’t.</p>
            </div>
            {/* Its own box under the card, as in the demo, not nested inside it.
                Only here: under a populated roster it would read as "absent
                means unpaid" (D6, sweep COPY-1), so it stays out of those. */}
            <p className="roster-note">A car that never started a session won’t appear here. Check its plate above.</p>
          </>
        ) : snap && shown.length === 0 && !checked && !checking && !(lookupError && counts.all > 0) ? (
          <p className="muted">
            {counts.all === 0 ? `No Parkify parking recorded in this ${zone.facility ? 'facility' : 'zone'} right now.` : 'Nothing in this filter.'}
          </p>
        ) : null}

        {/* An open result REPLACES the roster rather than sitting on top of it.
            Type to narrow, tap to decide, clear to browse again — restating the
            row the result came from directly underneath it is just two copies of
            the same answer competing on one screen. */}
        {!checked && (
          <ul className="rows">
            {shown.map(v => (
              <Row
                key={`${v.plate}-${v.source ?? 'x'}`}
                v={withLiveMinutes(v, nowMs)}
                typed={normalized}
                timezone={snap?.timezone ?? null}
                onOpen={isTappable(v) ? () => openResult(v) : undefined}
              />
            ))}
          </ul>
        )}

        {/* Not under an open result either: the card carries its own receipt line,
            and "Updated just now" beneath it would date the roster, not the answer. */}
        {snap && !checked && (
          <p className="footer">
            Updated {ageLabel(ageSec)} ({new Date(snap.asOf).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })})
            {snap.arrivalGraceMinutes && graceApplies ? ` · ${snap.arrivalGraceMinutes} min to pay after arriving` : ''}
            {' · Roster covers the last 2 hours. Search a plate for anything older.'}
          </p>
        )}

        {/* The words on a row, defined. Outside the list on purpose: a row is not a control
            (only an expired row opens anything). See <Glossary>. */}
        {snap && !checked && <Glossary />}

        {/* Reachable from the working screen at any width. The picker's Sign out
            is only reachable through the Back chevron, which a phone hides, so
            a staff member with a remembered lot (the shared or borrowed phone
            case) would otherwise have no way to end their session. Hidden
            while a result is open, as in the demo, so it never competes with
            the answer on screen. */}
        {onSignOut && !checked && (
          <div className="roster__signout">
            <button type="button" className="roster__signout-btn" onClick={onSignOut}>Sign out</button>
          </div>
        )}
      </main>
    </div>
  )
}

/**
 * Plates this lot holds that are one honest misread away from what was typed.
 *
 * Deliberately NOT auto-applied. The server can tell that MNP2210 is one
 * character from MNP2216; it cannot tell which one is bolted to the car the
 * officer is standing in front of. So this resolves nothing on its own — it
 * hands over the comparison, and the officer's eyes close it.
 *
 * The heading states the stakes plainly rather than saying "suggestions",
 * because the whole point is the one case where acting on the literal answer
 * would boot a driver who paid.
 */
function NearMisses({ typed, verdict, candidates, onPick }: {
  typed: string
  verdict: Verdict
  candidates: Candidate[]
  onPick: (c: Candidate) => void
}) {
  return (
    <section className="nearmiss" aria-labelledby="nearmiss-label">
      <p className="nearmiss__label" id="nearmiss-label">Similar plates in this {candidates.some(c => c.zoneName) ? 'facility' : 'zone'}</p>
      {/* Two sentences, as in the demo: a plate with no record is told nothing
          matches exactly, and any other answer is told to make sure the car is not
          one of these before enforcing. */}
      <p className="nearmiss__lead">
        {verdict === 'no_record'
          ? 'Nothing matches exactly, but these are close. Compare with the plate on the vehicle, if one matches, tap it.'
          : 'Before enforcing, make sure the vehicle isn’t one of these. Tap the plate that matches the car.'}
      </p>
      <ul className="nearmiss__list">
        {candidates.map(c => {
          const tone = toneFor(c.verdict)
          return (
            <li key={`${c.plate}-${c.source ?? 'x'}`} className={`row row--${tone} row--tappable`}>
              <button
                type="button" className="row__hit" onClick={() => onPick(c)}
                aria-label={candidateAccessibleName(c)}
              >
                <span className="row__identity">
                  <span className="row__identity-line">
                    <span className="plate">
                      <DiffPlate typed={typed} plate={c.plate} />
                    </span>
                    <PlateStateMarkView v={c} />
                  </span>
                  {/* The reason names the character to look at: "0 read as O"
                      sends the eye to one position; a score does not. It sits
                      under the plate it describes, on the left, as in the demo. */}
                  <span className="row__detail">{capitalizeReason(c.reason)}</span>
                </span>
                <span className="row__right">
                  <span className={`verdict verdict--${tone}`}>{labelFor(c)}</span>
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/**
 * A candidate plate with the characters that differ from what was typed marked.
 *
 * The reason line already names the confusion ("0 read as O"); this points at
 * the position. Marking is what turns "these two look similar" into "look at
 * character four", which is the whole comparison the officer is being asked to
 * make against the physical plate.
 *
 * Every character stays selectable and in order, and the marks carry no meaning
 * beyond "this one moved" — a screen reader gets the plate, unchanged.
 */
/**
 * A roster plate while the officer is narrowing: what they typed stays full
 * strength and the rest drops back. No new colour, because the near-miss wash
 * already means "differs from what you typed" and must not be reused for the
 * opposite. Same single-wrapper rule as DiffPlate, so the plate stays one string.
 */
function MatchedPlate({ plate, at, len }: { plate: string; at: number; len: number }) {
  let n = -1
  return (
    <span className="plate__chars">
      {plate.split('').map((ch, i) => {
        if (/[A-Z0-9]/.test(ch)) n++
        const on = /[A-Z0-9]/.test(ch) && n >= at && n < at + len
        return <span key={i} className={on ? undefined : 'plate__dim'}>{ch}</span>
      })}
    </span>
  )
}

function DiffPlate({ typed, plate }: { typed: string; plate: string }) {
  const mask = plateDiffMask(typed, plate)
  // ONE wrapper whose text content is exactly the plate. Without it the marked
  // characters are loose children of `.plate`, which also holds the jurisdiction
  // mark — so the nearest element containing the plate also contains "GA STATED",
  // and the plate stops being addressable as a single string by a test, a screen
  // reader, or a find-in-page.
  return (
    <span className="plate__chars">
      {plate.split('').map((ch, i) => (
        <span key={i} className={mask[i] ? 'plate__diff' : undefined}>{ch}</span>
      ))}
    </span>
  )
}

/**
 * The words on a row, defined (the dictionary Maecy asked for, 2026-10-15). A native <details>, so
 * it opens by tap on a phone as well as by click; the entries are only rendered while it is open,
 * so a closed panel puts no definitions in the page: a screen reader or a find-in-page does not
 * meet "Lot closed" or a second "STATED" on a lot that has neither. Reports what Parkify holds;
 * never says whether to cite.
 */
function Glossary() {
  const [open, setOpen] = useState(false)
  return (
    <details className="glossary" open={open} onToggle={e => setOpen(e.currentTarget.open)}>
      <summary className="glossary__summary">What do these words mean?</summary>
      {open && (
        <dl className="glossary__list">
          {glossaryEntries().map(({ term, meaning }) => (
            <div key={term} className="glossary__item">
              <dt>{term}</dt>
              <dd>{meaning}</dd>
            </div>
          ))}
        </dl>
      )}
    </details>
  )
}

/**
 * The plate's issuing jurisdiction and where it came from, beside the plate.
 *
 * `GA · STATED` rather than a bare `GA`, because the state alone is the
 * confidently-wrong data this exists to prevent: the checkout selector is
 * prefilled from the lot, so most stored states were never touched by anyone.
 *
 * Reports only — no guidance, deliberately. See the note on `PlateStateMark`.
 */
function PlateStateMarkView({ v, explain = false }: { v: Vehicle; explain?: boolean }) {
  const mark = plateStateMarkFor(v)
  if (!mark) return null
  const label = plateStateMarkLabel(mark)
  return (
    <span className="platestate" title={plateStateMarkTitle(mark)}>
      {/* "· NC", the demo's quiet qualifier: the middle dot reads the code as a
          note on the plate, not as part of it. The dot is decoration, so it is out
          of the accessible name; the code keeps its own text. */}
      {v.plateState && <span className="platestate__code"><span aria-hidden>· </span>{v.plateState}</span>}
      <span className={`platestate__mark platestate__mark--${mark}`}>{label}</span>
      {/* `title` is a hover tooltip: a touch screen reader never exposes it. On
          the result card the explanation is also real text. Rows and near-miss
          buttons carry the mark in their own accessible name instead. */}
      {explain && <span className="sr-only">. {plateStateMarkTitle(mark)}</span>}
    </span>
  )
}

/**
 * A roster row. Only an expired session is a deliberate entry point to the
 * detail view (Austin's demo, owner decision 2026-10-01): every other row
 * already says everything the result card would, so a tap would restate it.
 * Grace is deliberately NOT tappable: it must look exactly like an authorized
 * row, and a row that answers a tap differently would reveal it. A plate can
 * still be typed and checked for any car, and an exact roster match still
 * answers from the snapshot, so nothing becomes unreachable.
 */
function isTappable(v: Vehicle): boolean {
  return v.verdict === 'expired'
}

function Row({ v, typed = '', timezone = null, onOpen }: { v: Vehicle; typed?: string; timezone?: string | null; onOpen?: () => void }) {
  const tone = toneFor(v.verdict)
  const match = typed ? rosterMatch(typed, v.plate) : null
  const authorized = v.verdict === 'authorized' || v.verdict === 'grace'
  const inner = (
    <>
      <span className="row__identity">
        <span className="row__identity-line">
          <span className="plate">{match && match.len > 0 && match.len < v.plate.replace(/[^A-Z0-9]/g, '').length ? <MatchedPlate plate={v.plate} at={match.at} len={match.len} /> : v.plate}</span>
          <PlateStateMarkView v={v} />
          {authorized && (v.source === 'permit' || v.source === 'monthly') && (
            <span className="row__source">{labelFor(v)}</span>
          )}
          {v.zoneNames && v.zoneNames.length > 0 && <span className="row__zone">{v.zoneNames.join(' · ')}</span>}
        </span>
        {/* A closed row's second line is the operator's free-text closure reason,
            which has no length limit. The banner above and the card on tap already
            carry it in full, so the row clamps it (CSS) and keeps the whole text in
            `title`. */}
        <span
          className={`row__detail${endingSoon(v) ? ' row__detail--soon' : ''}${v.verdict === 'closed' ? ' row__detail--closed' : ''}`}
          title={v.verdict === 'closed' ? subLabelFor(v, timezone) : undefined}
        >{keepTimesTogether(subLabelFor(v, timezone))}</span>
      </span>
      <span className="row__right">
        {/* `title` is a hover tooltip, not a control: a plain row has no button and no focus
            stop (tests/Roster.rowInteractivity), so the tap-friendly version of this is the
            "What do these words mean?" panel under the list. Authorized and grace carry none,
            so a grace row cannot be told from an authorized one by hovering it. */}
        <span className={`verdict verdict--${tone}`} title={authorized ? undefined : verdictMeaning(v)}>{authorized ? 'Authorized' : labelFor(v)}</span>
      </span>
    </>
  )
  if (!onOpen) return <li className={`row row--${tone}`}>{inner}</li>
  return (
    <li className={`row row--${tone} row--tappable`}>
      {/* A real <button> rather than an onClick on the <li>: this is the one
          interactive thing on the screen, and an officer is using it one-handed
          in a lot — it has to take focus and fire on Enter/Space like a control. */}
      <button type="button" className="row__hit" onClick={onOpen} aria-label={rowAccessibleName(v, timezone)}>
        {inner}
      </button>
    </li>
  )
}

/**
 * The bay answer. Tone follows what the officer should DO, not what the bay is:
 * a reserved bay whose holder's plate is the one typed AND whose permit is in
 * force is fine (ok); a reserved bay holding some other plate is the
 * reserved-bay violation this feature exists to surface (bad); a holder who
 * matches but is outside their permit dates or hours is neither (warn); free is
 * neutral (info); out of service says "don't act on this line" (warn); unknown
 * bay and unknown say the same (unknown).
 *
 * `ok` is the colour the app reserves for "leave it alone", so it is only ever
 * painted when we can vouch for the car being allowed in that space right now.
 *
 * A reserved bay with NO plates is the Worker's "permit record unavailable"
 * shape (bay_assignments.ts: it deliberately will not call the bay free, and
 * says the officer should not tow into a bay it cannot vouch for). Nothing can
 * be compared against an empty list, so asserting "not on this bay's permit"
 * would state as fact something nobody read. It renders as unknown instead.
 */
function BayResultView({ r, typedPlate, plateFinal }: {
  r: BayResult
  typedPlate: string
  /**
   * The officer has asked for an answer on this plate (Check or Enter), so what is
   * typed is the whole plate. Until then it may be half of one.
   */
  plateFinal: boolean
}) {
  const held = r.status === 'reserved' && !!r.reservedFor
  const holderUnknown = r.status === 'reserved' && (!r.reservedFor || r.reservedFor.plates.length === 0)
  const holderPlates = held && !holderUnknown ? r.reservedFor!.plates.map(p => p.replace(/[^A-Z0-9]/g, '')) : []
  const plateMatches = holderPlates.length > 0 && !!typedPlate && holderPlates.includes(typedPlate)
  // A half-typed plate: still the front of the holder's plate, so more typing can
  // yet make it a match. The card stays neutral rather than flashing red "Reserved
  // for someone else" at every keystroke of the right plate. It goes red the moment
  // the typed characters can no longer become the holder's plate, and at once for
  // any plate the officer has asked about, however short. It never goes green early:
  // green still needs the whole plate.
  const stillTyping = !plateFinal && !plateMatches && !!typedPlate && holderPlates.some(p => p.startsWith(typedPlate))
  const outOfForce = held && !holderUnknown && !r.reservedFor!.inForce
  // The typed plate is a different car's: whole, or no longer able to become the holder's.
  const otherPlate = !!typedPlate && !plateMatches && !stillTyping
  const tone = r.status === 'reserved'
    ? (holderUnknown ? 'unknown'
      : typedPlate && !stillTyping ? (plateMatches ? (outOfForce ? 'warn' : 'ok') : 'bad')
      : 'info')
    : r.status === 'free' ? 'info'
    : r.status === 'out_of_service' ? 'warn'
    : 'unknown'
  const verdict = r.status === 'reserved'
    ? (holderUnknown ? 'Reserved, holder unknown'
      : otherPlate ? 'Reserved for someone else'
      : plateMatches && outOfForce ? 'Reserved, holder not in force'
      : 'Reserved bay')
    : r.status === 'free' ? 'Not reserved'
    : r.status === 'out_of_service' ? 'Out of service'
    : r.status === 'unknown_bay' ? 'No such bay' : 'Bay status unknown'
  return (
    <div className={`result result--${tone}`} role="status" data-bay-status={r.status}>
      <div className="result__plate">Bay {r.label}{r.section ? <span className="result__section"> · {r.section}</span> : null}</div>
      <div className="result__verdict">{verdict}</div>
      <div className="result__detail">
        {r.headline}
        {holderUnknown && ' Permit record unavailable. Do not act on this bay.'}
        {held && !holderUnknown && otherPlate && (
          <> {typedPlate} is not on this bay’s permit. Check the plate result below before acting. The car may still be authorized in the lot, just not in this space.</>
        )}
        {outOfForce && ' The holder is outside their permit dates or hours right now, so the bay is held but its holder is not authorized at this moment.'}
        {r.status === 'unknown' && ' Do not treat this bay as free.'}
      </div>
    </div>
  )
}

/**
 * `text` with the first occurrence of each phrase in `bold` set in <strong>. Only
 * weight changes: the text content is exactly `text`, in order, so a phrase that is
 * not found (or overlaps an earlier one) is skipped rather than dropping or
 * repeating a word.
 */
function emphasise(text: string, bold: string[]): React.ReactNode {
  const spans: Array<[number, number]> = []
  for (const phrase of bold) {
    const at = phrase ? text.indexOf(phrase) : -1
    if (at >= 0 && !spans.some(([a, b]) => at < b && at + phrase.length > a)) spans.push([at, at + phrase.length])
  }
  if (spans.length === 0) return text
  spans.sort((x, y) => x[0] - y[0])
  const out: React.ReactNode[] = []
  let pos = 0
  spans.forEach(([a, b], i) => {
    if (a > pos) out.push(text.slice(pos, a))
    out.push(<strong key={i}>{text.slice(a, b)}</strong>)
    pos = b
  })
  if (pos < text.length) out.push(text.slice(pos))
  return out
}

/** The zones a facility entry spans, e.g. "All of GA634 · GA644". */
function FacilityZonesLine({ zones }: { zones: { id: string; name: string }[] }) {
  return <span className="lot__zones">All of {zones.map(z => z.name).join(' · ')}</span>
}

/**
 * Every zone's own answer under a facility card. The card leads with the answer
 * that matters (any zone that authorizes wins), and this list is the rest of the
 * picture, so an officer who knows which zone the car is standing in can read
 * that zone's verdict directly.
 */
function FacilityVerdicts({ zones, plate }: { zones: FacilityZoneAnswer[]; plate: string }) {
  const anyAuthorized = zones.some(z => z.vehicle.verdict === 'authorized' || z.vehicle.verdict === 'grace')
  return (
    <section className="facility-verdicts" aria-label={`${plate} in each zone`}>
      <h3 className="facility-verdicts__title">Each zone</h3>
      <ul className="facility-verdicts__list">
        {zones.map(z => {
          const authorized = z.vehicle.verdict === 'authorized' || z.vehicle.verdict === 'grace'
          // Once another zone authorizes, this zone's miss is context, not a
          // violation: grey, so a red chip under a green card doesn't read as "ticket".
          const tone = !authorized && anyAuthorized ? 'unknown' : toneFor(z.vehicle.verdict)
          return (
            <li key={z.zoneId} className={`facility-verdicts__row row--${tone}`}>
              <span className="facility-verdicts__zone">{z.zoneName}</span>
              <span className={`verdict verdict--${tone}`}>{authorized ? 'Authorized' : labelFor(z.vehicle)}</span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function PlateResult({ v, arrivalGrace, timezone = null, nowMs, provenance, zoneName, enteredAs, innerRef }: {
  v: Vehicle
  /** Focus target: opening a result moves focus here. */
  innerRef?: React.Ref<HTMLDivElement>
  arrivalGrace: number | null
  timezone?: string | null
  /** The roster's clock, for dating a lapse that was not today. */
  nowMs?: number
  /**
   * Where this answer came from and when. Null while a lookup is still in
   * flight for the card on screen, which renders as "Rechecking".
   */
  provenance: Provenance | null
  zoneName: string
  /** What the officer typed, when they reached this answer via a near-miss. */
  enteredAs: string | null
}) {
  const tone = resultToneFor(v.verdict)
  const icon = v.verdict === 'authorized' ? '✓'
    : v.verdict === 'grace' ? '—'
    : v.verdict === 'pending' || v.verdict === 'unverified' ? '?'
    : v.verdict === 'closed' ? 'i' : '×'
  return (
    <div className={`result result--plate result--${tone}`} role="status" ref={innerRef} tabIndex={-1} aria-labelledby="plate-result-verdict">
      <div className="result__heading">
        <span className="result__icon" aria-hidden="true">{icon}</span>
        <div className="result__identity">
          <h2 className="result__verdict" id="plate-result-verdict">{headlineFor(v)}</h2>
          <div className="result__plate">
            {v.plate}
            <PlateStateMarkView v={v} explain />
          </div>
          {v.zoneNames && v.zoneNames.length > 0 && <div className="result__zone">{facilityZoneLine(v)}</div>}
        </div>
      </div>
      {(() => {
        const g = guidanceFor(v, arrivalGrace, timezone)
        // A closed card's closing "No session is expected here" is a quieter line of
        // its own; the two parts are still the server's whole sentence.
        const { main, note } = v.verdict === 'closed' ? splitClosedGuidance(g) : { main: g, note: null }
        // Every other card, as in the demo: the figure to act on in bold, and a second
        // fact (the expiry clock, the pending caveat) on a quieter grey line. Same
        // words as `guidanceFor`, which is what the screen reader announces.
        const parts = v.verdict === 'closed'
          ? { main, bold: [] as string[], second: null }
          : guidanceParts(v, arrivalGrace, timezone)
        // The absolute time of what already happened, under the elapsed sentence.
        const lapse = lapseLine(v, timezone, nowMs)
        return <>
          <div className="result__detail">{emphasise(keepTimesTogether(parts.main), parts.bold.map(keepTimesTogether))}</div>
          {parts.second && <>{' '}<div className="result__detail result__detail--second">{keepTimesTogether(parts.second)}</div></>}
          {note && <>{' '}<div className="result__detail result__detail--note">{note}</div></>}
          {lapse && <div className="result__detail result__detail--lapsed">{keepTimesTogether(lapse)}</div>}
        </>
      })()}
      {/* The cheapest possible record: a line the officer can screenshot.
          What it says is bounded by what we can actually stand behind — see
          the note on `ResultProvenance`. */}
      {provenance ? (
        <ResultProvenance provenance={provenance} zoneName={zoneName} enteredAs={enteredAs} plate={v.plate} />
      ) : (
        <p className="result__provenance result__provenance--pending">Rechecking {v.plate}…</p>
      )}
    </div>
  )
}

/**
 * The line under a plate answer saying when it was obtained, for which lot, and
 * — when the officer got here by tapping a near-miss — what they originally
 * entered.
 *
 * WHY THIS EXISTS: the near-miss list is the one place patrol invites the
 * officer to act on a plate OTHER than the one they typed. Once the result
 * re-renders for the candidate, nothing on screen says a correction happened.
 * "We booted MNP2210" and "we booted MNP2210, reached from MNP2216 via the
 * similar-plates list" are different stories in a wrongful-tow dispute, and only
 * the second explains itself weeks later. This is the whole evidentiary value of
 * the feature, and it costs one line.
 *
 * ⚠️ WHAT IT DELIBERATELY DOES NOT SAY. Austin's demo ends this line with
 * "logged to <partner>". Production must not: `enforcement/patrol_router.ts` is
 * read-only BY CONSTRUCTION — it has no non-GET branch, calls `writeAuditEvent`
 * nowhere, and its own header says "there is nothing to audit and no way for a
 * partner to write". A line claiming the check was logged somewhere, on a screen
 * whose purpose is to be screenshotted as evidence, would be a fabricated
 * record — worse than no line at all, because it would be believed.
 *
 * So this states only what is true of the artifact in the officer's hand: the
 * time on their own device, the lot they picked, and the correction they made.
 * It also never says "Checked" for an answer no lookup returned: a roster answer
 * is dated by the snapshot ("Roster as of"), so the receipt cannot claim a
 * fresher check than the one that happened.
 * If server-side lookup logging is ever built, THEN this line may say so.
 */
function ResultProvenance({ provenance, zoneName, enteredAs, plate }: {
  provenance: Provenance
  zoneName: string
  enteredAs: string | null
  plate: string
}) {
  // A pick that resolves back to the same plate is not a correction. Comparing
  // guards the case where the officer taps a candidate identical to what they
  // typed (a duplicate row, or a re-pick after clearing) — recording "entered as
  // X" against an answer for X reads as a correction that never happened.
  const corrected = enteredAs && enteredAs !== plate ? enteredAs : null
  return (
    <p className="result__provenance">
      {provenance.kind === 'roster' ? 'Roster as of' : 'Checked'}{' '}
      {provenance.at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} · {zoneName}
      {corrected && <> · entered as <span className="result__entered">{corrected}</span> · matched by officer</>}
    </p>
  )
}
